
import _ from 'lodash';

const namespaced = true;

const SHORTCUT_FILTERS = [
  { id: `new`, name: `Nouvelles interventions` },
  { id: `notFinished`, name: `Interventions non terminées` },
  { id: `finished`, name: `Interventions terminées` },
  { id: `all`, name: `Tous status` },
];

const SORTS = [
  { id: `updatedAt:desc`, label: `Date de modification - plus récente en premier` },
  { id: `updatedAt:asc`, label: `Date de modification - plus ancienne en premier` },
  { id: `createdAt:desc`, label: `Date de création - plus récente en premier` },
  { id: `createdAt:asc`, label: `Date de création - plus ancienne en premier` },
  { id: `num:desc`, label: `Numéro d'intervention - plus récente en premier` },
  { id: `num:asc`, label: `Numéro d'intervention - plus ancienne en premier` },
];

const SORT_ID_LS_KEY = `indeego:interventions:sortId`;
const LAYOUT_LS_KEY = `indeego:interventions:layout`;

const LAYOUTS = {
  list: {
    label: `Liste`,
    defaultNbItemsPerPage: 10,
    defaultFiltersOpened: true,
    lsKey: `${LAYOUT_LS_KEY}:list`,
  },
  table: {
    label: `Tableau`,
    defaultNbItemsPerPage: 50,
    defaultFiltersOpened: false,
    lsKey: `${LAYOUT_LS_KEY}:table`,
  },
};

const DEFAULT_LAYOUT = `list`;

const getStateLayoutOrDefault = state => {
  let layout = DEFAULT_LAYOUT;
  const lsLayout = window.localStorage.getItem(LAYOUT_LS_KEY);

  if (state.layout) {
    layout = state.layout;
  } else if (lsLayout) {
    layout = lsLayout;
  }

  return layout;
};

const state = {
  list: [],
  challengingList: [],
  dbTags: [],
  dbEquipments: [],
  priorities: [],
  domains: [],
  shortcutFilters: SHORTCUT_FILTERS,
  shortcutFilter: SHORTCUT_FILTERS[0].id,
  sorts: SORTS,
  sortId: null,
  status: null,
  page: 1,
  maker: null,
  client: null,
  site: null,
  domain: null,
  priority: null,
  tags: [],
  equipment: null,
  layout: null,
  availableLayouts: _.reduce(LAYOUTS, (acc, layout, key) => {
    acc.push({
      ...layout,
      id: key,
    });
    return acc;
  }, []),
  layouts: {
    ..._.reduce(LAYOUTS, (acc, layout, key) => {
      acc[key] = {
        nbItemsPerPage: null,
        areFiltersOpened: null,
      };
      return acc;
    }, {}),
  },
};

const mutations = {
  SET_CHALLENGING_LIST(state, challengingList) {
    state.challengingList = challengingList;
  },
  SET_LIST(state, list) {
    state.list = list;
  },
  SET_PRIORITIES(state, priorities) {
    state.priorities = priorities;
  },
  SET_DOMAINS(state, domains) {
    state.domains = domains;
  },
  SET_DB_TAGS(state, tags) {
    state.dbTags = tags;
  },
  SET_DB_EQUIPMENTS(state, equipments) {
    state.dbEquipments = equipments;
  },
  SET_SHORTCUT_FILTER(state, shortcutFilter) {
    state.shortcutFilter = shortcutFilter;
  },
  SET_STATUS(state, status) {
    state.status = status;
  },
  SET_SORT_ID(state, sortId) {
    state.sortId = sortId;
    window.localStorage.setItem(SORT_ID_LS_KEY, sortId);
  },
  SET_FILTERS_OPENED(state, areFiltersOpened) {
    const layout = getStateLayoutOrDefault(state);
    state.layouts[layout].areFiltersOpened = areFiltersOpened;
    state.filtersOpened = areFiltersOpened;
    const lsKey = `${LAYOUTS[layout].lsKey}:filtersOpened`;
    window.localStorage.setItem(lsKey, areFiltersOpened ? `yes` : `no`);
  },
  SET_LAYOUT(state, layout) {
    let targetLayout = layout;
    if (!LAYOUTS[layout]) {
      targetLayout = DEFAULT_LAYOUT;
    }
    state.layout = targetLayout;
    window.localStorage.setItem(LAYOUT_LS_KEY, targetLayout);
  },
  SET_NB_ITEMS_PER_PAGE(state, nbItemsPerPage) {
    const layout = getStateLayoutOrDefault(state);
    state.layouts[layout].nbItemsPerPage = nbItemsPerPage;
    const lsKey = `${LAYOUTS[layout].lsKey}:nbItemsPerPage`;
    window.localStorage.setItem(lsKey, nbItemsPerPage);
  },
  SET_PAGE(state, page) {
    state.page = page;
  },
  SET_MAKER(state, maker) {
    state.maker = maker;
  },
  SET_CLIENT(state, client) {
    state.client = client;
  },
  SET_SITE(state, site) {
    state.site = site;
  },
  SET_DOMAIN(state, domain) {
    state.domain = domain;
  },
  SET_PRIORITY(state, priority) {
    state.priority = priority;
  },
  SET_TAGS(state, tags) {
    state.tags = tags;
  },
  SET_EQUIPMENT(state, equipment) {
    state.equipment = equipment;
  },
};

const actions = {
  async fetchList({
    state,
    commit,
    dispatch,
    getters,
  }) {
    const queryParams = {
      sort: getters.currentSort.id,
      page: state.page,
      limit: getters.currentNbItemsPerPage,
    };

    if (state.status && state.status !== `all`) {
      if (_.findIndex(SHORTCUT_FILTERS, { id: state.status }) !== -1) {
        queryParams.statusFilter = state.status;
      } else {
        queryParams.statusFilter = `id`;
        queryParams.status = state.status;
      }
    }
    if (state.maker) { queryParams.maker = state.maker; }
    if (state.client) { queryParams.client = state.client; }
    if (state.site) { queryParams.site = state.site; }
    if (state.domain) { queryParams.domain = state.domain; }
    if (state.priority) { queryParams.priority = state.priority; }
    if (state.tags && state.tags.length) {
      queryParams.tags = state.tags.join(`,`);
    }
    if (state.equipment) { queryParams.equipment = state.equipment; }

    const [
      challengingInterventions,
      interventions,
    ] = await Promise.all([
      dispatch(`model/challengingIntervention/fetchList`, { queryParams }, { root: true }),
      dispatch(`model/intervention/fetchList`, { queryParams }, { root: true }),
    ]);
    commit(`SET_CHALLENGING_LIST`, challengingInterventions);
    commit(`SET_LIST`, interventions);
  },
  forceFetchList({ dispatch }) {
    return dispatch(`fetchList`);
  },
  async fetchPriorities({ commit, dispatch, state }) {
    let priorities = [];
    if (state.maker) {
      priorities = await dispatch(`model/priority/fetchPrioritiesForMaker`, state.maker, { root: true });
    }

    commit(`SET_PRIORITIES`, priorities);
    return priorities;
  },
  async fetchDomains({ commit, dispatch, state }) {
    let domains = [];
    if (state.maker) {
      domains = await dispatch(`model/domain/fetchDomainsForMaker`, state.maker, { root: true });
    }

    commit(`SET_DOMAINS`, domains);
    return domains;
  },
  async fetchClientTags({ commit, dispatch }, clientId) {
    const tags = await dispatch(`model/tag/fetchForClient`, clientId, { root: true });

    commit(`SET_DB_TAGS`, tags);
    return tags;
  },
  async fetchDbTags({ commit, dispatch, state }) {
    let tags = [];
    if (state.maker) {
      tags = await dispatch(`model/tag/fetchForMaker`, state.maker, { root: true });
    }

    commit(`SET_DB_TAGS`, tags);
    return tags;
  },
  async fetchEquipments({ commit, dispatch, state }) {
    let equipments = [];
    if (state.client) {
      const queryParams = {
        client: state.client,
        viewAll: 1,
      };
      if (state.site) {
        queryParams.site = state.site;
      }
      equipments = await dispatch(`model/equipment/fetchList`, { queryParams }, { root: true });
    }

    commit(`SET_DB_EQUIPMENTS`, equipments);
    return equipments;
  },
};

const getters = {
  defaultSortId: (state, getters, rootState) => {
    const { company } = rootState[`model/client`];
    if (company.slug === `lefroidvendeen`) {
      return `createdAt:desc`;
    }

    return `updatedAt:desc`;
  },
  currentSort: (state, getters) => {
    let sort;
    const lsSortId = window.localStorage.getItem(SORT_ID_LS_KEY);

    if (state.sortId) {
      sort = _.find(state.sorts, { id: state.sortId });
    } else if (lsSortId) {
      sort = _.find(state.sorts, { id: lsSortId });
    } else {
      sort = _.find(state.sorts, { id: getters.defaultSortId });
    }

    return sort;
  },
  currentFiltersOpened: (state, getters) => {
    const layout = getters.currentLayout;
    let areFiltersOpened = LAYOUTS[layout].defaultFiltersOpened;
    const lsKey = `${LAYOUTS[layout].lsKey}:filtersOpened`;
    const lsFiltersOpened = window.localStorage.getItem(lsKey);

    if (state.layouts[layout].areFiltersOpened) {
      areFiltersOpened = state.layouts[layout].areFiltersOpened;
    } else if (lsFiltersOpened) {
      areFiltersOpened = lsFiltersOpened === `yes`;
    }

    return areFiltersOpened;
  },
  currentLayout: state => getStateLayoutOrDefault(state),
  currentNbItemsPerPage: (state, getters) => {
    const layout = getters.currentLayout;
    let nbItemsPerPage = LAYOUTS[layout].defaultNbItemsPerPage;
    const lsKey = `${LAYOUTS[layout].lsKey}:nbItemsPerPage`;
    const lsNbItemsPerPage = window.localStorage.getItem(lsKey);

    let nbItemsPerPageCandidate = null;
    if (state.layouts[layout].nbItemsPerPage) {
      nbItemsPerPageCandidate = state.layouts[layout].nbItemsPerPage;
    } else if (lsNbItemsPerPage) {
      nbItemsPerPageCandidate = parseInt(lsNbItemsPerPage, 10);
    }

    if (nbItemsPerPageCandidate && typeof nbItemsPerPageCandidate === `number` && nbItemsPerPageCandidate > 0) {
      nbItemsPerPage = nbItemsPerPageCandidate;
    }

    return nbItemsPerPage;
  },
};

export default {
  namespaced, state, mutations, actions, getters,
};
